import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";

import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import { FuelProductIcon } from "@components/Icons";
import { formatBRL } from "@utils/currency";

import type { ICartView } from "../../../model/product.cart.model";

import {
  CatalogOption,
  parseDotQuantity,
  useProductsStep,
} from "../../hook/useProductsStep";

export function ProductsStepMobile({ guardTick }: { guardTick?: number }) {
  const { state, action } = useProductsStep();

  const allCash = useMemo(
    () => state.cartView.every((c) => c.payment === "T"),
    [state.cartView]
  );

  const disabled = state.pricingStatus === "loading" || !state.canInteract;

  const [selected, setSelected] = useState<CatalogOption | null>(null);

  const [flash, setFlash] = useState(false);

  async function onSelect(opt: CatalogOption | null) {
    setSelected(opt);
    if (!opt) return;

    setSelected(null);
    void action.addProduct({
      productId: opt.key,
      code: opt.code ?? opt.key,
      description: opt.label,
      quantity: 0,
    });
  }

  useEffect(() => {
    const tick = guardTick ?? 0;

    if (tick <= 0) return;
    setFlash(false);
    const raf = requestAnimationFrame(() => setFlash(true));
    const t = setTimeout(() => setFlash(false), 450);

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [guardTick]);

  return (
    <Box sx={{ display: "grid", gap: 2 }}>
      {state.pricingStatus === "loading" && (
        <Box sx={{ mt: 1 }}>
          <LinearProgress />
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Calculando preço e atualizando carrinho...
          </Typography>
        </Box>
      )}
      <TextField
        select
        label="Descrição do produto"
        value={selected?.key ?? ""}
        onChange={(e) => {
          const next =
            state.catalogOptions.find((o) => o.key === e.target.value) ?? null;
          onSelect(next);
        }}
        disabled={disabled}
        fullWidth
      >
        {state.catalogOptions.length === 0 ? (
          <MenuItem value="" disabled>
            Nenhum produto disponível
          </MenuItem>
        ) : (
          state.catalogOptions.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))
        )}
      </TextField>

      {!state.canInteract ? (
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Selecione cliente, endereço e base antes de adicionar produtos.
        </Typography>
      ) : null}

      {state.error ? (
        <Typography variant="body2" color="error">
          {state.error}
        </Typography>
      ) : null}

      {state.cartView.map((it: ICartView) => {
        const qtyInvalid = !Number.isFinite(it.quantity) || it.quantity === 0;
        const shouldAnimate = flash && qtyInvalid;
        return (
          <Card
            key={it.id}
            variant="outlined"
            sx={{
              borderRadius: 3,
              p: 2,
              borderColor: qtyInvalid ? "error.main" : "divider",
              borderWidth: qtyInvalid ? 1 : 0,
              transition: "all 0.2s ease-in-out",
              boxShadow: qtyInvalid ? "0 0 8px rgba(211, 47, 47, 0.1)" : "none",

              ...(shouldAnimate && {
                animation: "shake 0.35s ease-in-out",
                boxShadow: "0 0 0 3px rgba(211,47,47,0.18)",
              }),

              "@keyframes shake": {
                "0%": { transform: "translateX(0)" },
                "25%": { transform: "translateX(-6px)" },
                "50%": { transform: "translateX(6px)" },
                "75%": { transform: "translateX(-4px)" },
                "100%": { transform: "translateX(0)" },
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: "rgba(255,0,0,0.06)",
                }}
              >
                <FuelProductIcon fontSize="small" />
              </Box>

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography fontWeight={800} noWrap>
                  {it.description}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Código: {it.code}
                </Typography>
              </Box>

              <IconButton
                onClick={() => action.remove(it.id)}
                aria-label="remover"
              >
                <DeleteOutlineIcon color="error" />
              </IconButton>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Valor do litro
                </Typography>
                <Typography fontWeight={800}>
                  {formatBRL(it.displayUnitPrice, { fractionDigits: 4 })}
                </Typography>
              </Box>

              <Box sx={{ textAlign: "right" }}>
                <Typography variant="caption" sx={{ opacity: 0.7 }}>
                  Subtotal
                </Typography>
                <Typography fontWeight={900} color="success.main">
                  {formatBRL(it.subtotal, { fractionDigits: 2 })}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
                mt: 3,
              }}
            >
              <TextField
                select
                label="Quantidade"
                value={String(it.quantity)}
                onChange={(e) => void action.changeQty(it.id, e.target.value)}
                disabled={state.pricingStatus === "loading"}
                helperText={qtyInvalid ? "Selecione uma quantidade" : ""}
                sx={{
                  minWidth: 145,
                  "& .MuiFormHelperText-root": {
                    color: "primary.main",
                  },
                }}
                fullWidth
              >
                {state.quantityOptions.map((opt) => (
                  <MenuItem key={opt.value} value={parseDotQuantity(opt.label)}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>

              {!allCash && state.isCashPaymentEnabled && it.payment !== "T" && (
                <TextField
                  select
                  label="Prazo"
                  value={String(it.deadlinePayment)}
                  onChange={(e) =>
                    void action.changeDeadline(it.id, e.target.value)
                  }
                  disabled={state.pricingStatus === "loading"}
                  fullWidth
                >
                  {it.paymentDayOptions
                    .filter((o) =>
                      it.payment === "B" ? Number(o.key) > 0 : true
                    )
                    .map((o) => {
                      const days = Number(o.key);
                      return (
                        <MenuItem key={o.key} value={String(days)}>
                          {days === 0
                            ? "A vista"
                            : `${days} ${days === 1 ? "Dia" : "Dias"}`}
                        </MenuItem>
                      );
                    })}
                </TextField>
              )}
            </Box>
          </Card>
        );
      })}
    </Box>
  );
}
